import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi.js";

/**
 * Mapeo de localidades por ID - debe coincidir con wa-cloud-bridge/src/utils/localidad.js
 */
const LOCALIDADES = {
  1: "Madryn",
  2: "Trelew",
  3: "Rawson",
  4: "Gaiman",
  5: "Comodoro",
  6: "Pico Truncado",
  7: "Las Heras",
  8: "Casilda",
  9: "Dolavon",
  10: "Carcaraña",
  11: "Caleta Olivia",
  12: "El Calafate"
};

const LOCALIDADES_OPTIONS = Object.entries(LOCALIDADES).map(([id, nombre]) => ({
  id: parseInt(id, 10),
  nombre
}));

const ALERT_TYPES = [
  { value: "outage", label: "Falla masiva", description: "Interrupción del servicio no programada" },
  { value: "maintenance", label: "Trabajo programado", description: "Mantenimiento planificado" },
  { value: "info", label: "Información general", description: "Aviso informativo" }
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatDateTimeForInput(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function getAlertStatus(alert) {
  if (!alert.isActive) {
    if (alert.closedAt) {
      return { label: "Cerrada", className: "status-closed" };
    }
    return { label: "Desactivada", className: "status-inactive" };
  }
  const now = new Date();
  const start = new Date(alert.startDate);
  const end = new Date(alert.endDate);
  if (now < start) return { label: "Programada", className: "status-scheduled" };
  if (now > end) return { label: "Finalizada", className: "status-ended" };
  return { label: "Activa", className: "status-active" };
}

function AlertForm({ alert, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(alert?.title || "");
  const [message, setMessage] = useState(alert?.message || "");
  const [alertType, setAlertType] = useState(alert?.alertType || "outage");
  const [localidades, setLocalidades] = useState(alert?.localidades || []);
  const [startDate, setStartDate] = useState(formatDateTimeForInput(alert?.startDate) || "");
  const [endDate, setEndDate] = useState(formatDateTimeForInput(alert?.endDate) || "");
  const [isActive, setIsActive] = useState(alert?.isActive ?? true);
  const [error, setError] = useState("");

  const isEditing = Boolean(alert?.id);

  const handleLocalidadToggle = useCallback((id) => {
    setLocalidades((prev) => {
      if (prev.includes(id)) {
        return prev.filter((l) => l !== id);
      }
      return [...prev, id];
    });
  }, []);

  const handleSelectAllLocalidades = useCallback(() => {
    const allIds = LOCALIDADES_OPTIONS.map((l) => l.id);
    setLocalidades((prev) => {
      if (prev.length === allIds.length) {
        return [];
      }
      return allIds;
    });
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setError("");

      if (!title.trim()) {
        setError("El título es obligatorio");
        return;
      }
      if (!message.trim()) {
        setError("El mensaje es obligatorio");
        return;
      }
      if (!startDate) {
        setError("La fecha de inicio es obligatoria");
        return;
      }
      if (!endDate) {
        setError("La fecha de fin es obligatoria");
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start >= end) {
        setError("La fecha de fin debe ser posterior a la fecha de inicio");
        return;
      }

      onSave({
        id: alert?.id,
        title: title.trim(),
        message: message.trim(),
        alertType,
        localidades,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        isActive
      });
    },
    [alert?.id, title, message, alertType, localidades, startDate, endDate, isActive, onSave]
  );

  return (
    <form className="alert-form" onSubmit={handleSubmit}>
      <h3>{isEditing ? "Editar alerta" : "Nueva alerta"}</h3>

      {error && <div className="alert-form-error">{error}</div>}

      <div className="form-group">
        <label htmlFor="alert-title">Título</label>
        <input
          id="alert-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ej: Corte de servicio zona norte"
          maxLength={100}
        />
      </div>

      <div className="form-group">
        <label htmlFor="alert-message">Mensaje para el cliente</label>
        <textarea
          id="alert-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ej: ⚠️ Estamos trabajando para restablecer el servicio en tu zona. Estimamos normalizar en las próximas horas. Disculpá las molestias."
          rows={4}
          maxLength={1000}
        />
        <span className="form-hint">Este mensaje se enviará automáticamente a los clientes afectados.</span>
      </div>

      <div className="form-group">
        <label htmlFor="alert-type">Tipo de alerta</label>
        <select
          id="alert-type"
          value={alertType}
          onChange={(e) => setAlertType(e.target.value)}
        >
          {ALERT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Localidades afectadas</label>
        <div className="localidades-selector">
          <button
            type="button"
            className="button-secondary localidades-toggle-all"
            onClick={handleSelectAllLocalidades}
          >
            {localidades.length === LOCALIDADES_OPTIONS.length ? "Desmarcar todas" : "Seleccionar todas"}
          </button>
          <div className="localidades-grid">
            {LOCALIDADES_OPTIONS.map((loc) => (
              <label key={loc.id} className="localidad-checkbox">
                <input
                  type="checkbox"
                  checked={localidades.includes(loc.id)}
                  onChange={() => handleLocalidadToggle(loc.id)}
                />
                <span>{loc.nombre}</span>
              </label>
            ))}
          </div>
        </div>
        <span className="form-hint">
          {localidades.length === 0
            ? "Sin localidades seleccionadas = aplica a TODAS las localidades"
            : `${localidades.length} localidad(es) seleccionada(s)`}
        </span>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="alert-start">Fecha y hora de inicio</label>
          <input
            id="alert-start"
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="alert-end">Fecha y hora de fin</label>
          <input
            id="alert-end"
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {isEditing && (
        <div className="form-group">
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>Alerta activa</span>
          </label>
        </div>
      )}

      <div className="form-actions">
        <button type="button" className="button-secondary" onClick={onCancel} disabled={saving}>
          Cancelar
        </button>
        <button type="submit" className="button-primary" disabled={saving}>
          {saving ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear alerta"}
        </button>
      </div>
    </form>
  );
}

/**
 * Formulario para cerrar una alerta con mensaje de resolución
 */
function CloseAlertForm({ alert, onClose, onCancel, closing }) {
  const [resolutionMessage, setResolutionMessage] = useState("");
  const [notifyRecipients, setNotifyRecipients] = useState(true);
  const [error, setError] = useState("");

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setError("");

      if (notifyRecipients && !resolutionMessage.trim()) {
        setError("El mensaje de resolución es obligatorio si se va a notificar a los clientes");
        return;
      }

      onClose({
        resolutionMessage: resolutionMessage.trim(),
        notifyRecipients
      });
    },
    [resolutionMessage, notifyRecipients, onClose]
  );

  return (
    <form className="alert-form" onSubmit={handleSubmit}>
      <h3>Cerrar alerta: {alert.title}</h3>

      {error && <div className="alert-form-error">{error}</div>}

      <div className="close-alert-info">
        <p>
          Esta acción cerrará la alerta y dejará de enviarse a nuevos clientes.
        </p>
      </div>

      <div className="form-group">
        <label className="checkbox-inline">
          <input
            type="checkbox"
            checked={notifyRecipients}
            onChange={(e) => setNotifyRecipients(e.target.checked)}
          />
          <span>Notificar a los clientes que recibieron la alerta</span>
        </label>
        <span className="form-hint">
          Se enviará un mensaje a todos los clientes que fueron notificados de esta alerta.
        </span>
      </div>

      {notifyRecipients && (
        <div className="form-group">
          <label htmlFor="resolution-message">Mensaje de resolución</label>
          <textarea
            id="resolution-message"
            value={resolutionMessage}
            onChange={(e) => setResolutionMessage(e.target.value)}
            placeholder="Ej: ✅ Informamos que el servicio ha sido restablecido en tu zona. Gracias por tu paciencia."
            rows={4}
            maxLength={1000}
          />
          <span className="form-hint">
            Este mensaje se enviará a los clientes que recibieron la alerta original.
          </span>
        </div>
      )}

      <div className="form-actions">
        <button type="button" className="button-secondary" onClick={onCancel} disabled={closing}>
          Cancelar
        </button>
        <button type="submit" className="button-primary" disabled={closing}>
          {closing ? "Cerrando..." : notifyRecipients ? "Cerrar y notificar" : "Cerrar sin notificar"}
        </button>
      </div>
    </form>
  );
}

function AlertListItem({ alert, onEdit, onDelete, onCloseAlert, deleting, canClose }) {
  const status = getAlertStatus(alert);
  const localidadLabels = useMemo(() => {
    if (!alert.localidades || alert.localidades.length === 0) {
      return "Todas las localidades";
    }
    return alert.localidades.map((id) => LOCALIDADES[id] || `ID ${id}`).join(", ");
  }, [alert.localidades]);

  const alertTypeLabel = useMemo(() => {
    const type = ALERT_TYPES.find((t) => t.value === alert.alertType);
    return type?.label || alert.alertType;
  }, [alert.alertType]);

  return (
    <div className={`alert-list-item ${status.className}`}>
      <div className="alert-item-header">
        <div className="alert-item-title">
          <h4>{alert.title}</h4>
          <span className={`alert-status-badge ${status.className}`}>{status.label}</span>
          <span className="alert-type-badge">{alertTypeLabel}</span>
        </div>
        <div className="alert-item-actions">
          {canClose && (
            <button
              type="button"
              className="button-primary"
              onClick={() => onCloseAlert(alert)}
              title="Cerrar alerta"
            >
              Cerrar
            </button>
          )}
          <button
            type="button"
            className="button-secondary"
            onClick={() => onEdit(alert)}
            title="Editar"
          >
            Editar
          </button>
          <button
            type="button"
            className="button-danger"
            onClick={() => onDelete(alert.id)}
            disabled={deleting}
            title="Eliminar"
          >
            Eliminar
          </button>
        </div>
      </div>
      <div className="alert-item-body">
        <p className="alert-message-preview">{alert.message}</p>
        {alert.resolutionMessage && (
          <div className="alert-resolution">
            <strong>Mensaje de resolución:</strong>
            <p className="alert-message-preview">{alert.resolutionMessage}</p>
          </div>
        )}
        <div className="alert-item-meta">
          <span>
            <strong>Inicio:</strong> {formatDateTime(alert.startDate)}
          </span>
          <span>
            <strong>Fin:</strong> {formatDateTime(alert.endDate)}
          </span>
          {alert.closedAt && (
            <span>
              <strong>Cerrada:</strong> {formatDateTime(alert.closedAt)}
            </span>
          )}
          <span>
            <strong>Localidades:</strong> {localidadLabels}
          </span>
          {alert.createdByName && (
            <span>
              <strong>Creado por:</strong> {alert.createdByName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ServiceAlertsModal({ open, onClose, onToast }) {
  const api = useApi();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [closingAlert, setClosingAlert] = useState(null);
  const [filterActive, setFilterActive] = useState(false);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterActive ? { active: "true" } : {};
      const { data } = await api.get("/alerts", { params });
      setAlerts(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      onToast?.(error.response?.data?.message || "Error al cargar alertas");
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [api, filterActive, onToast]);

  useEffect(() => {
    if (open) {
      loadAlerts();
      setShowForm(false);
      setShowCloseForm(false);
      setEditingAlert(null);
      setClosingAlert(null);
    }
  }, [open, loadAlerts]);

  const handleCreateNew = useCallback(() => {
    setEditingAlert(null);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((alert) => {
    setEditingAlert(alert);
    setShowForm(true);
  }, []);

  const handleCancelForm = useCallback(() => {
    setShowForm(false);
    setEditingAlert(null);
  }, []);

  const handleSave = useCallback(
    async (alertData) => {
      try {
        setSaving(true);
        if (alertData.id) {
          // Actualizar
          await api.put(`/alerts/${alertData.id}`, alertData);
          onToast?.("Alerta actualizada correctamente");
        } else {
          // Crear
          await api.post("/alerts", alertData);
          onToast?.("Alerta creada correctamente");
        }
        setShowForm(false);
        setEditingAlert(null);
        loadAlerts();
      } catch (error) {
        onToast?.(error.response?.data?.message || "Error al guardar la alerta");
      } finally {
        setSaving(false);
      }
    },
    [api, loadAlerts, onToast]
  );

  const handleDelete = useCallback(
    async (alertId) => {
      if (!window.confirm("¿Estás seguro de eliminar esta alerta?")) {
        return;
      }
      try {
        setDeleting(true);
        await api.delete(`/alerts/${alertId}`);
        onToast?.("Alerta eliminada correctamente");
        loadAlerts();
      } catch (error) {
        onToast?.(error.response?.data?.message || "Error al eliminar la alerta");
      } finally {
        setDeleting(false);
      }
    },
    [api, loadAlerts, onToast]
  );

  const handleCloseAlert = useCallback((alert) => {
    setClosingAlert(alert);
    setShowCloseForm(true);
  }, []);

  const handleCancelCloseForm = useCallback(() => {
    setShowCloseForm(false);
    setClosingAlert(null);
  }, []);

  const handleCloseSubmit = useCallback(
    async ({ resolutionMessage, notifyRecipients }) => {
      if (!closingAlert) return;
      try {
        setClosing(true);
        const { data } = await api.post(`/alerts/${closingAlert.id}/close`, {
          resolutionMessage,
          notifyRecipients
        });
        
        let msg = "Alerta cerrada correctamente";
        if (notifyRecipients && data.notification) {
          const notif = data.notification;
          
          // Respuesta asíncrona (envío en background)
          if (notif.async && notif.jobId) {
            msg = `Alerta cerrada. ${notif.message || "Notificaciones en proceso..."}`;
            // Podríamos iniciar polling aquí para mostrar progreso
            onToast?.(msg);
            setShowCloseForm(false);
            setClosingAlert(null);
            loadAlerts();
            return;
          }
          
          // Respuesta síncrona
          const { sent = 0, skipped = 0, failed = 0, attempted = 0 } = notif;
          if (attempted === 0) {
            msg = "Alerta cerrada. No había destinatarios para notificar.";
          } else if (skipped > 0) {
            msg = `Alerta cerrada. Notificados: ${sent}/${attempted}. ${skipped} omitidos (fuera de ventana 24h).`;
          } else if (failed > 0) {
            msg = `Alerta cerrada. Notificados: ${sent}/${attempted}. ${failed} fallidos.`;
          } else {
            msg = `Alerta cerrada. ${sent} notificaciones enviadas.`;
          }
        }
        
        onToast?.(msg);
        setShowCloseForm(false);
        setClosingAlert(null);
        loadAlerts();
      } catch (error) {
        onToast?.(error.response?.data?.message || "Error al cerrar la alerta");
      } finally {
        setClosing(false);
      }
    },
    [api, closingAlert, loadAlerts, onToast]
  );

  /**
   * Determina si una alerta puede ser cerrada manualmente
   * Solo alertas activas que no hayan sido cerradas previamente
   */
  const canCloseAlert = useCallback((alert) => {
    if (!alert.isActive || alert.closedAt) return false;
    const now = new Date();
    const start = new Date(alert.startDate);
    const end = new Date(alert.endDate);
    // Solo puede cerrarse si está actualmente en progreso o programada
    return now >= start && now <= end;
  }, []);

  const filteredAlerts = useMemo(() => {
    return alerts;
  }, [alerts]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal admin-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2>Alertas de servicio</h2>
          <button className="button-secondary" onClick={onClose} type="button">
            Cerrar
          </button>
        </header>

        <div className="modal-body">
          {showCloseForm && closingAlert ? (
            <CloseAlertForm
              alert={closingAlert}
              onClose={handleCloseSubmit}
              onCancel={handleCancelCloseForm}
              closing={closing}
            />
          ) : showForm ? (
            <AlertForm
              alert={editingAlert}
              onSave={handleSave}
              onCancel={handleCancelForm}
              saving={saving}
            />
          ) : (
            <>
              <div className="alerts-toolbar">
                <div className="alerts-toolbar-left">
                  <button
                    type="button"
                    className="button-primary"
                    onClick={handleCreateNew}
                  >
                    + Nueva alerta
                  </button>
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={filterActive}
                      onChange={(e) => setFilterActive(e.target.checked)}
                    />
                    <span>Solo activas</span>
                  </label>
                </div>
                <button
                  type="button"
                  className="button-secondary"
                  onClick={loadAlerts}
                  disabled={loading}
                >
                  {loading ? "Cargando..." : "Actualizar"}
                </button>
              </div>

              <div className="alerts-description">
                <p>
                  Configurá alertas para comunicar fallas masivas o trabajos programados.
                  Los clientes afectados recibirán el mensaje automáticamente en su primera
                  interacción durante el período de la alerta.
                </p>
              </div>

              {loading ? (
                <div className="alerts-loading">Cargando alertas...</div>
              ) : filteredAlerts.length === 0 ? (
                <div className="alerts-empty">
                  <p>No hay alertas configuradas.</p>
                  <p>Creá una nueva alerta para comunicar fallas o trabajos programados.</p>
                </div>
              ) : (
                <div className="alerts-list">
                  {filteredAlerts.map((alert) => (
                    <AlertListItem
                      key={alert.id}
                      alert={alert}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onCloseAlert={handleCloseAlert}
                      deleting={deleting}
                      canClose={canCloseAlert(alert)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}